import React, { createContext, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getFirebase } from 'react-redux-firebase';
import type { AppState } from '../config/store';
import type { SurveyTag } from '../types/survey';
import { getSafeTextFromText } from '../routes/client-portal/reporting/create/report-types/photo-schedule/pdf/helpers';
import { NUM_MAX_SURVEY_TAGS } from '../config/constants';

type Tags = SurveyTag[];

type OnOpenProps = {
  selectedTags?: Tags;
  id: string;
};

type TagsSelectorProps = {
  open: ({ selectedTags, id }: OnOpenProps) => void;
  close: () => void;
  isOpen: boolean;
  saveTags: () => void;
  onToggleTag: (tag: SurveyTag) => void;
  tags: Tags;
  selectedTags: Tags;
  removeTag: (tag: SurveyTag, allTags: Tags, id: string) => void;
  addTag: (tagName: string) => void;
  onTagAdded?: (tag: SurveyTag) => void;
  deleteTag: (tag: SurveyTag) => void;
};

export const CORE_SURVEY_TAGS = [
  {
    label: 'QA Stage 1',
    id: 'qa-stage-1',
    uid: '0',
    userName: 'INTERNAL',
    added: 0,
    removed: 0,
    core: true,
  },
  {
    label: 'QA Stage 2',
    id: 'qa-stage-2',
    uid: '0',
    userName: 'INTERNAL',
    added: 0,
    removed: 0,
    core: true,
  },
  {
    label: 'QA Stage 3',
    id: 'qa-stage-3',
    uid: '0',
    userName: 'INTERNAL',
    added: 0,
    removed: 0,
    core: true,
  },
  {
    label: 'Final',
    id: 'final',
    uid: '0',
    userName: 'INTERNAL',
    added: 0,
    removed: 0,
    core: true,
  },
  {
    label: 'Issued',
    id: 'issued',
    uid: '0',
    userName: 'INTERNAL',
    added: 0,
    removed: 0,
    core: true,
  },
  {
    label: 'Accepted',
    id: 'accepted',
    uid: '0',
    userName: 'INTERNAL',
    added: 0,
    removed: 0,
    core: true,
  },
  {
    label: 'Rejected',
    id: 'rejected',
    uid: '0',
    userName: 'INTERNAL',
    added: 0,
    removed: 0,
    core: true,
  },
];

const TagsSelectorContext = createContext<TagsSelectorProps | undefined>(
  undefined,
);

type TagsSelectorProviderProps = {
  children: React.ReactNode;
  onSave: ({ tags, id }: { tags: Tags; id: string }) => void;
  onTagAdded?: (tag: SurveyTag) => void;
  onTagDeleted?: (tag: SurveyTag) => void;
};

function TagsSelectorProvider({
  children,
  onSave,
  onTagAdded,
  onTagDeleted,
}: TagsSelectorProviderProps) {
  const client = useSelector((state: AppState) => state.firestore.data.client);
  const [id, setId] = useState<string>('0');
  const [tags, setTags] = useState<Tags>([...CORE_SURVEY_TAGS]);
  const [initialTags, setInitialTags] = useState<Tags>([]);
  const [selectedTags, setSelectedTags] = useState<Tags>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { currentUser } = getFirebase().auth();
  const profile = useSelector((state: AppState) => state.firebase.profile);

  const userDetails = {
    uid: currentUser?.uid || '0',
    userName: `${profile.nameFirst} ${profile.nameLast}`,
  };

  const open = useCallback(
    ({ selectedTags, id }: OnOpenProps) => {
      setId(id);
      const selected = selectedTags || [];
      const baseTags = [...CORE_SURVEY_TAGS, ...(client.surveyTags || [])];
      const addToCore = selected.filter(
        (tag) => !baseTags.find((t) => t.id === tag.id),
      );
      if (addToCore.length) {
        setTags([...baseTags, ...addToCore]);
      } else {
        setTags([...baseTags]);
      }
      setSelectedTags([...selected]);
      setInitialTags([...selected]);
      setIsOpen(true);
    },
    [client.surveyTags],
  );

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onToggleTag = useCallback(
    (tag: SurveyTag) => {
      const exists = selectedTags.find((t) => t.id === tag.id);
      let newSelectedTags: Tags = [];

      if (exists) {
        newSelectedTags = selectedTags.filter((t) => t.id !== tag.id);
      } else {
        if (selectedTags.length >= NUM_MAX_SURVEY_TAGS) {
          return;
        }
        newSelectedTags = [...selectedTags, tag];
      }

      setSelectedTags(newSelectedTags);
    },
    [selectedTags],
  );

  const removeTag = useCallback(
    (tag: SurveyTag, allTags: Tags, id: string) => {
      const newSelectedTags = allTags.filter((t) => t.id !== tag.id);
      onSave({ tags: newSelectedTags, id });
    },
    [selectedTags],
  );

  const deleteTag = useCallback(
    (tag: SurveyTag) => {
      const newTags = tags.filter((t) => t.id !== tag.id);
      setTags(newTags);
      const newSelectedTags = selectedTags.filter((t) => t.id !== tag.id);
      setSelectedTags(newSelectedTags);
      if (onTagDeleted) {
        onTagDeleted(tag);
      }
    },
    [tags, selectedTags],
  );

  const addTag = useCallback(
    (label: string) => {
      const id = getSafeTextFromText(label);
      if (tags.find((t) => t.id === id)) {
        return;
      }
      const newTag = {
        label,
        id: getSafeTextFromText(label),
        ...userDetails,
        added: Date.now(),
        core: false,
      };
      setTags([...tags, newTag]);
      onTagAdded?.(newTag);
    },
    [tags],
  );

  const saveTags = useCallback(() => {
    if (!currentUser) {
      return;
    }
    const updatedTags = selectedTags.map((tag) => {
      return tag.uid === '0'
        ? {
            ...tag,
            uid: currentUser.uid,
            userName: `${profile.nameFirst} ${profile.nameLast}`,
            added: Date.now(),
            id: tag.id || getSafeTextFromText(tag.label),
          }
        : tag;
    });
    //check if the tags have changed, if not, then close the dialog without calling onSave
    if (
      updatedTags.length === initialTags.length &&
      updatedTags.every((tag) => initialTags.find((t) => t.id === tag.id))
    ) {
      close();
      return;
    }
    setSelectedTags(updatedTags);
    onSave({ tags: updatedTags, id });
    close();
  }, [id, selectedTags, onSave, close]);

  const value = useMemo(
    () => ({
      open,
      close,
      isOpen,
      saveTags,
      onToggleTag,
      tags,
      selectedTags,
      removeTag,
      addTag,
      onTagAdded,
      deleteTag,
      onTagDeleted,
    }),
    [
      open,
      close,
      isOpen,
      saveTags,
      onToggleTag,
      tags,
      selectedTags,
      removeTag,
      addTag,
      onTagAdded,
      deleteTag,
      onTagDeleted,
    ],
  );
  return (
    <TagsSelectorContext.Provider value={value}>
      {children}
    </TagsSelectorContext.Provider>
  );
}

function useTagsSelector() {
  const context = React.useContext(TagsSelectorContext);

  if (context === undefined) {
    throw new Error(
      'useTagsSelector must be used within a TagsSelectorProvider',
    );
  }

  return context;
}
export { TagsSelectorProvider, useTagsSelector };
